<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    :create="create"
    title="Criminal History"
    :section="section"
    has-footer
    has-documents
    :document-types="documentTypes"
    :document-identifier="documentIdentifier"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col
          cols="8"
        >
          <FormatSelectableComboDate
            class="mt-3 ml-1"
            label="Issue Date"
            :formatable-date="section.incident_date"
            :format="section.incident_date_format"
            :uncertain="section.incident_date_uncertain"
            required
            outlined
            :disabled="locked"
            for-party-edit
            :error-messages="getErrorMessages($v.section.incident_date)"
            @update-box="onIncidentDateUncertainUpdate"
            @update-date="onIncidentDateUpdate"
            @tab-out="$v.section.incident_date.$touch()"
          />
        </v-col>
        <v-col
          cols="4"
        >
          <v-text-field
            v-model="section.incident_location"
            autocomplete="off"
            label="Incident Location"
            outlined
            :error-messages="getErrorMessages($v.section.incident_location)"
            @input="$v.section.incident_location.$touch()"
            @blur="$v.section.incident_location.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <!-- text-area notes -->
        <v-col>
          <v-textarea
            v-model="section.notes"
            label="Notes"
            rows="4"
            outlined
            :error-messages="getErrorMessages($v.section.notes)"
            @input="$v.section.notes.$touch()"
            @blur="$v.section.notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { SHARED_NAMESPACE } from '@/store/shared'

import { alertHandler } from '@/alertHandler'

import FormatSelectableComboDate from '@/components/Fields/FormatSelectableComboDate'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'CriminalHistoriesCard',
  components: { FormatSelectableComboDate, GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      documentTypes: [
        { text: 'Court Record', value: 'court-record' },
        { text: 'Police Report', value: 'police-report' },
        { text: 'Other Record', value: 'other-record' },
      ],

      section: {},
    }
  },

  validations () {
    return {
      section: {
        incident_date: {},
        incident_date_uncertain: {},
        incident_location: {},
        notes: {},
      },
    }
  },

  computed: {
    cardIdentifier () { return `criminal-history-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },
    documentIdentifier () { return `history-criminal-history-${this.section.id}` },

    locked: {
      get () {
        return this.peCriminalHistoriesCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeCriminalHistoriesCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    ...mapGetters(PARTY_NAMESPACE, [
      'peCriminalHistoriesCardLock',
      'peParty',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'getCachedDropdown',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPeCriminalHistoriesCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peCriminalHistoriesCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peCriminalHistoriesCardCreateUndo()
      else {
        return this.peCriminalHistoriesCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.peCriminalHistoriesCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peCriminalHistoriesCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    onIncidentDateUncertainUpdate (value) {
      this.$v.section.incident_date.$touch()
      this.$v.section.incident_date_uncertain.$touch()
      this.section.incident_date_uncertain = value
    },

    onIncidentDateUpdate (value) {
      this.$v.section.incident_date.$touch()
      this.$v.section.incident_date_uncertain.$touch()
      this.section.incident_date = value.date
      this.section.incident_date_format = value.format
    },

    async onSectionUnlock () {
      return this.setPeCriminalHistoriesCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peCriminalHistoriesCardCreateUndo',
      'setPeCriminalHistoriesCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeCriminalHistoriesCard',
      'peCriminalHistoriesCardCreate',
      'peCriminalHistoriesCardDelete',
      'peCriminalHistoriesCardRefresh',
      'peCriminalHistoriesCardUpdate',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getDropdownOptions',
    ]),
  },
}
</script>
